import React, { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../../api/axiosInstance';
import ofimaSyncService from '../../../api/ofimaSyncService';
import { OrderComparison } from '../../../types/comparative';

interface OFIMASyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  missingOrders: OrderComparison[];
  onSyncComplete?: () => void;
}

interface SyncResult {
  orderId: string;
  error?: string;
  ofimaResponse?: any;
  status?: number;
}

interface SyncResponse {
  message: string;
  results: {
    success: any[];
    failed: SyncResult[];
  };
}

const OFIMASyncModal: React.FC<OFIMASyncModalProps> = ({
  isOpen,
  onClose,
  missingOrders,
  onSyncComplete
}) => {
  const [currentStep, setCurrentStep] = useState<'idle' | 'sending' | 'spx' | 'force-sync' | 'completed' | 'error'>('idle');
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<SyncResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [networkError, setNetworkError] = useState<boolean>(false);
  const [ignoredOrders, setIgnoredOrders] = useState<OrderComparison[]>([]);
  const [spxMessage, setSpxMessage] = useState<string | null>(null);
  const [spxError, setSpxError] = useState<string | null>(null);

  // Función para verificar si un pedido debe ser ignorado
  const isOrderCancelled = useCallback((order: OrderComparison): boolean => {
    if (!order.sglOrder) return false;
    // Ignorar pedidos con estados que no deben sincronizarse
    return order.sglOrder.status === 'eliminado' || 
           order.sglOrder.status === 'verify_transaction' || 
           order.sglOrder.status === 'cancelled' ||
           order.sglOrder.status === 'pending';
  }, []);

  // Función para dividir array en chunks de 3
  const chunkArray = (array: any[], size: number) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  // Función para enviar pedidos a OFIMA
  const sendToOFIMA = async (orderIds: string[]): Promise<SyncResponse> => {
    try {
    const response = await axiosInstance.post('/ofima/send-to-ofima', {
      orderIds
    });
    return response.data;
    } catch (error: any) {
      console.error('Error enviando pedidos a OFIMA:', error);
      // Crear un error simple sin propiedades adicionales para evitar problemas de React
      const errorMessage = error.response?.data?.message || 'Error enviando pedidos a OFIMA';
      const statusCode = error.response?.status || 500;
      throw new Error(`${errorMessage} (${statusCode})`);
    }
  };

  // Función para ejecutar SPX
  const ejecutarSPX = async () => {
    try {
      const response = await ofimaSyncService.ejecutarSPX();
      return response;
    } catch (error: any) {
      console.error('Error ejecutando SPX:', error);
      // Crear un error con propiedades personalizadas
      const customError = new Error(error.message || 'Error ejecutando SPX');
      (customError as any).error = error.message;
      (customError as any).status = error.response?.status || 500;
      throw customError;
    }
  };

  // Función para ejecutar sincronizacion de remisiones
  const forceSyncAll = async () => {
    try {
      const response = await ofimaSyncService.forceSyncAll();
      return response;
    } catch (error: any) {
      console.error('Error en sincronizacion de remisiones:', error);
      // Crear un error simple sin propiedades adicionales para evitar problemas de React
      const errorMessage = error.message || 'Error en sincronizacion de remisiones';
      throw new Error(errorMessage);
    }
  };

  // Función principal de sincronización
  const handleSync = useCallback(async () => {
    if (missingOrders.length === 0) return;

    setIsProcessing(true);
    setError(null);
    setNetworkError(false);
    setIgnoredOrders([]);
    setSpxMessage(null);
    setSpxError(null);
    setCurrentStep('sending');
    setProgress(0);

    try {
      // Filtrar pedidos cancelados y separar los que se van a ignorar
      const cancelledOrders = missingOrders.filter(isOrderCancelled);
      const validOrders = missingOrders.filter(order => 
        order.sglOrder && !isOrderCancelled(order)
      );
      
      setIgnoredOrders(cancelledOrders);

      // Si no hay pedidos válidos para sincronizar
      if (validOrders.length === 0) {
        setCurrentStep('completed');
        setProgress(100);
        return;
      }

      // 1. Enviar pedidos a OFIMA en lotes de 3
      const orderIds = validOrders
        .map(order => order.sglOrder!.id.toString());
      const chunks = chunkArray(orderIds, 3);
      let allResults: SyncResponse = { message: '', results: { success: [], failed: [] } };

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        setProgress((i / chunks.length) * 30); // 30% para envío
        
        try {
          const result = await sendToOFIMA(chunk);
          allResults.results.success.push(...result.results.success);
          allResults.results.failed.push(...result.results.failed);
        } catch (error: any) {
          console.error('Error enviando chunk:', error);
          // Agregar errores específicos para este chunk
          let errorMessage = 'Error de conexión al enviar a OFIMA';
          
          if (error.message && error.message.includes('404')) {
            errorMessage = 'Servicio no disponible (404)';
            setNetworkError(true);
          } else if (error.message && error.message.includes('NETWORK_ERROR')) {
            errorMessage = 'Error de conexión de red';
            setNetworkError(true);
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          chunk.forEach(orderId => {
            allResults.results.failed.push({
              orderId,
              error: errorMessage
            });
          });
        }
      }

      setResults(allResults);

      // 2. Si al menos uno fue exitoso, ejecutar SPX
      if (allResults.results.success.length > 0) {
        setCurrentStep('spx');
        setProgress(50);
        
        try {
          const spxResponse = await ejecutarSPX();
          // Mostrar el mensaje de éxito de SPX
          if (spxResponse?.message) {
            setSpxMessage(spxResponse.message);
            // Esperar un momento para que el usuario vea el mensaje
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
          setProgress(70);
        } catch (error: any) {
          console.error('Error ejecutando SPX:', error);
          
          // Manejar error de SPX con detalles específicos
          if (error.message && error.error) {
            // Error con detalles del backend - solo mostrar en sección de SPX
            setSpxError(`${error.message}: ${error.error}`);
            // No establecer error general para evitar duplicación
          } else if (error.message && error.message.includes('404')) {
            setSpxError('Servicio SPX no disponible (404)');
            setNetworkError(true);
          } else if (error.message && error.message.includes('NETWORK_ERROR')) {
            setSpxError('Error de conexión de red en SPX');
            setNetworkError(true);
          } else if (error.message) {
            setSpxError(error.message);
          } else {
            setSpxError('Error ejecutando SPX');
          }
          
          setCurrentStep('error');
          return;
        }

        // 3. Ejecutar sincronizacion de remisiones
        setCurrentStep('force-sync');
        setProgress(80);
        
        try {
          await forceSyncAll();
          setProgress(100);
          setCurrentStep('completed');
        } catch (error: any) {
          console.error('Error en sincronizacion de remisiones:', error);
          let errorMessage = 'Error en sincronizacion de remisiones';
          
          if (error.message && error.message.includes('404')) {
            errorMessage = 'Servicio sincronizacion no disponible (404)';
            setNetworkError(true);
          } else if (error.message && error.message.includes('NETWORK_ERROR')) {
            errorMessage = 'Error de conexión de red en sincronizacion de remisiones';
            setNetworkError(true);
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          setError(errorMessage);
          setCurrentStep('error');
          return;
        }
      } else {
        setCurrentStep('error');
        setError('No se pudo enviar ningún pedido a OFIMA');
      }

    } catch (error: any) {
      console.error('Error general:', error);
      let errorMessage = 'Error inesperado durante la sincronización';
      
      if (error.message && error.message.includes('404')) {
        errorMessage = 'Servicio no disponible (404)';
        setNetworkError(true);
      } else if (error.message && error.message.includes('NETWORK_ERROR')) {
        errorMessage = 'Error de conexión de red';
        setNetworkError(true);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setCurrentStep('error');
    } finally {
      setIsProcessing(false);
    }
  }, [missingOrders, isOrderCancelled]);

  // Ejecutar sincronización automáticamente al abrir el modal
  useEffect(() => {
    if (isOpen && missingOrders.length > 0) {
      handleSync();
    }
  }, [isOpen, handleSync, missingOrders.length]);

  const getStepIcon = (step: string) => {
    switch (step) {
      case 'sending':
        return <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-500" />;
      case 'spx':
        return <ArrowPathIcon className="w-6 h-6 animate-spin text-yellow-500" />;
      case 'force-sync':
        return <ArrowPathIcon className="w-6 h-6 animate-spin text-green-500" />;
      case 'completed':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" />;
      case 'error':
        return <XCircleIcon className="w-6 h-6 text-red-500" />;
      default:
        return <ExclamationTriangleIcon className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStepText = (step: string) => {
    switch (step) {
      case 'sending':
        return 'Enviando pedidos a OFIMA...';
      case 'spx':
        return 'Ejecutando SPX...';
      case 'force-sync':
        return 'Sincronizando remisiones...';
      case 'completed':
        return 'Sincronización completada';
      case 'error':
        return 'Error en la sincronización';
      default:
        return 'Preparando sincronización...';
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      if (currentStep === 'completed' && onSyncComplete) {
        onSyncComplete();
      }
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl p-6 m-4 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-6">
          <div>
          <h3 className="text-2xl font-semibold text-blue-800">
            Sincronización con OFIMA
          </h3>
            {missingOrders.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                {missingOrders.length} pedido{missingOrders.length !== 1 ? 's' : ''} pendiente{missingOrders.length !== 1 ? 's' : ''} de sincronización
                {ignoredOrders.length > 0 && (
                  <span className="text-yellow-600 ml-2">
                    ({ignoredOrders.length} ignorado{ignoredOrders.length !== 1 ? 's' : ''} por estado no válido)
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progreso */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {getStepText(currentStep)}
            </span>
            <span className="text-sm text-gray-500">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Estado actual */}
        <div className="flex items-center gap-3 mb-6">
          {getStepIcon(currentStep)}
          <span className="text-lg font-medium">
            {getStepText(currentStep)}
          </span>
        </div>

        {/* Mensaje de SPX */}
        {spxMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <span className="text-sm font-medium text-green-800 block mb-1">
                  SPX Ejecutado Exitosamente
                </span>
                <span className="text-sm text-green-700">{spxMessage}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error de SPX */}
        {spxError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <XCircleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <span className="text-sm font-medium text-red-800 block mb-1">
                  Error en SPX
                </span>
                <span className="text-xs text-red-700">{spxError}</span>
              </div>
            </div>
          </div>
        )}

        {/* Pedidos ignorados */}
        {ignoredOrders.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-yellow-800 mb-2 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-4 h-4" />
              Pedidos ignorados por estado no válido ({ignoredOrders.length})
            </h4>
            <div className="text-xs text-yellow-700">
              <p className="mb-2">Los siguientes pedidos fueron ignorados porque están en estados que no permiten sincronización (eliminado, verificar transacción, cancelado, pendiente de pago):</p>
              <div className="space-y-1">
                {ignoredOrders.map((order, index) => {
                  const getStatusLabel = (status: string) => {
                    switch (status) {
                      case 'eliminado': return 'Eliminado';
                      case 'verify_transaction': return 'Verificar Transacción';
                      case 'cancelled': return 'Cancelado';
                      case 'pending': return 'Pendiente de Pago';
                      default: return status;
                    }
                  };
                  
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <span className="font-mono">•</span>
                      <span>Pedido {order.sglOrder?.id} - {getStatusLabel(order.sglOrder?.status || '')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Resultados */}
        {results && (
          <div className="space-y-4">
            {/* Éxitos */}
            {results.results.success.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4" />
                  Pedidos enviados exitosamente ({results.results.success.length})
                </h4>
                <div className="text-xs text-green-700 flex flex-wrap gap-2">
                  {results.results.success.map((item, index) => (
                    <span key={index} className="font-mono bg-green-100 px-2 py-1 rounded">
                      {typeof item === 'string' ? item : item.orderId || item.id || 'N/A'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Errores */}
            {results.results.failed.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-red-800 mb-2 flex items-center gap-2">
                  <XCircleIcon className="w-4 h-4" />
                  Pedidos con error ({results.results.failed.length})
                </h4>
                <div className="space-y-1">
                  {results.results.failed.map((item, index) => {
                    // Acortar mensajes de error comunes
                    const getShortError = (error: string) => {
                      if (error.includes('cancelado')) return 'Cancelado';
                      if (error.includes('Código del Medio de Pago')) return 'Error: No tiene Método de Pago';
                      if (error.includes('Error al enviar')) return 'Error de envío';
                      return error.length > 50 ? error.substring(0, 47) + '...' : error;
                    };

                    return (
                      <div key={index} className="text-xs text-red-700 flex items-center gap-2">
                        <span className="font-mono font-medium text-red-800 min-w-[80px]">
                          #{item.orderId}
                        </span>
                        <span className="text-red-600 flex-1">
                          {getShortError(item.error || 'Error desconocido')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error general - solo mostrar si no hay error específico de SPX */}
        {error && !spxError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <XCircleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <span className="text-sm font-medium text-red-800 block mb-1">
                  Error en la sincronización
                </span>
                <span className="text-sm text-red-700">{error}</span>
                {error.includes('404') && (
                  <div className="mt-2 text-xs text-red-600 bg-red-100 p-2 rounded">
                    <strong>Nota:</strong> El servicio solicitado no está disponible. 
                    Verifica que el servidor esté funcionando correctamente.
                  </div>
                )}
                {networkError && (
                  <div className="mt-2 text-xs text-red-600 bg-red-100 p-2 rounded">
                    <strong>Error de conexión:</strong> No se pudo conectar con el servidor. 
                    Verifica tu conexión a internet y que el servidor esté disponible.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-end space-x-4 mt-6">
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentStep === 'completed' ? 'Cerrar' : 'Cancelar'}
          </button>
          {currentStep === 'error' && (
            <button
              onClick={handleSync}
              disabled={isProcessing}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reintentar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OFIMASyncModal;
